/**
 * AI-availability person and event properties.
 *
 * Whether Sona may be offered to a member (`available`, `opted_out`,
 * `restricted`) and, when restricted, why (`state` or `oud`). The server derives
 * the value; this module only names the analytics keys and maps a derived value
 * onto them, so no rule about who is restricted lives here.
 *
 * The derived value can carry the member's home state code when the reason is
 * `state`. That code is the member's own address data and never becomes an
 * analytics value: the mapping reads only the status and the reason.
 *
 * PostHog is BAA-covered and program context is allowed there (ADR-079 Rule 2),
 * so the reason is sent per person; Datadog gets only aggregate counts.
 */

export const AI_AVAILABILITY_STATUS_VALUES = ['available', 'opted_out', 'restricted'] as const;
export type AiAvailabilityStatusValue = (typeof AI_AVAILABILITY_STATUS_VALUES)[number];

export const AI_RESTRICTED_REASON_VALUES = ['state', 'oud'] as const;
export type AiRestrictedReasonValue = (typeof AI_RESTRICTED_REASON_VALUES)[number];

/** The metric-tag form of the reason: `none` for a member who is not restricted. */
export const AI_RESTRICTED_REASON_TAG_VALUES = ['state', 'oud', 'none'] as const;
export type AiRestrictedReasonTagValue = (typeof AI_RESTRICTED_REASON_TAG_VALUES)[number];

/** Person- and event-property keys as they appear in the analytics store. */
export const AI_AVAILABILITY_PERSON_PROPERTY_KEYS = {
  status: 'ai_availability_status',
  reason: 'ai_restricted_reason',
} as const;

/**
 * The shape this module reads from a derived availability. Structural, so the
 * shared schema's `AiAvailability` (which may also carry `state`) is accepted
 * without this package depending on it.
 */
export type AiAvailabilityInput =
  | { readonly status: 'available' }
  | { readonly status: 'opted_out' }
  | { readonly status: 'restricted'; readonly reason: AiRestrictedReasonValue };

export interface AiAvailabilityProperties {
  readonly [AI_AVAILABILITY_PERSON_PROPERTY_KEYS.status]: AiAvailabilityStatusValue;
  /** Null unless the status is `restricted`, which clears a reason set earlier. */
  readonly [AI_AVAILABILITY_PERSON_PROPERTY_KEYS.reason]: AiRestrictedReasonValue | null;
}

/** The two analytics properties for one derived availability. Never copies a state code. */
export function aiAvailabilityPersonProperties(
  availability: AiAvailabilityInput,
): AiAvailabilityProperties {
  return {
    [AI_AVAILABILITY_PERSON_PROPERTY_KEYS.status]: availability.status,
    [AI_AVAILABILITY_PERSON_PROPERTY_KEYS.reason]:
      availability.status === 'restricted' ? availability.reason : null,
  };
}

/** The reason as a closed metric-tag value. */
export function aiAvailabilityGaugeReason(
  availability: AiAvailabilityInput,
): AiRestrictedReasonTagValue {
  return availability.status === 'restricted' ? availability.reason : 'none';
}
